import { Pills, FoodsIcon, SearchBox, FoodsToggleButtonType } from "@foodpilot/foods";
import { DraggableViewTabs } from "./DraggableViewTabs";
import { arrayMove } from "@dnd-kit/sortable";
import { Stack, Box, Typography, Button, useTheme } from "@mui/material";
import { GridFilters } from "../hooks/gridUrlSync/useGridFiltersUrlSync";
import { Fragment } from "react/jsx-runtime";
import { useViewFilters } from "../hooks/viewFilters/useViewFilters";
import type { OneViewFilter } from "../hooks/viewFilters/toLocalStorage";
import { CreateViewButton } from "./CreateViewButton/CreateViewButton";
import { useTranslation } from "react-i18next";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { MRT_ColumnDef, MRT_RowData } from "material-react-table";
import { RangeValue } from "../filters/RangeFilter/RangeFilter";
import { ComparisonValue } from "../filters/ComparisonFilter/ComparisonFilter";
import { JsonAttributeFilterValue } from "../filters/JsonAttributeFilter/JsonAttributeFilter";
import type { TagsFilterValue } from "../filters/TagsFilter";
import { GridColumnsType } from "../hooks/columnsExtension/useGridColumns";
import { GridTableActions, TableAction } from "./GridTableActions";
import type { ActionsPopoverAppearance } from "src/components/ActionsPopover/ActionsPopover";

export type GridHeaderWithFiltersProps<DataT extends MRT_RowData> = {
    readonly gridKey: string;
    columns: MRT_ColumnDef<DataT, any>[];
    gridFilters: GridFilters;
    columnsConfig?: GridColumnsType<DataT>;
    searchProduct?: string;
    setSearchProduct?: (value: string) => void;
    tableActions?: TableAction[];
    tableActionsAppearance?: ActionsPopoverAppearance;
    viewTabsLeading?: ReactNode;
};
export const GridHeaderWithFilters = <DataT extends MRT_RowData>(props: GridHeaderWithFiltersProps<DataT>) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const {
        gridFilters,
        columns,
        gridKey,
        columnsConfig,
        searchProduct,
        setSearchProduct,
        tableActions = [],
        tableActionsAppearance = "legacy",
        viewTabsLeading,
    } = props;

    const columnById = useMemo(() => {
        const map = new Map<string, MRT_ColumnDef<DataT, any>>();
        for (const column of columns) {
            if (column.id !== undefined) map.set(column.id, column);
        }
        return map;
    }, [columns]);

    // Function to get display name for column ID
    const getColumnDisplayName = (columnId: string): string => {
        const foundColumn = columnById.get(columnId);
        if (foundColumn === undefined) {
            // At least, return something to display.
            // This should never happen, but just for defensive programming.
            return columnId;
        }
        return foundColumn.header;
    };

    // This is important because every-time we re-render the filters, we don't want the
    // selectedFilter to be changed. The selected filter must be dealt with at INIT only.
    // Then, it's handled internally
    const memoizedFilterColumns = useMemo(() => {
        return gridFilters;
    }, [gridFilters]);

    const viewFilter = useViewFilters({
        gridKey,
        gridFilters: memoizedFilterColumns,
        columnsConfig,
    });

    const initialSelected =
        viewFilter.selectedFilter === undefined ? undefined : { id: viewFilter.selectedFilter.filterName };

    const [selectedButton, _setSelectedButton] = useState(initialSelected);
    const [defaultView] = viewFilter.allFilters;
    const replacesDefaultViewTab = viewTabsLeading !== undefined && viewTabsLeading !== null;
    const applyView = (view: OneViewFilter) => {
        viewFilter.selectView(view);
        gridFilters.setColumnFilters(view.columnFilters);
    };
    const viewButtons: FoodsToggleButtonType[] = viewFilter.allFilters.map((view) => {
        const isActiveView = view.filterName === viewFilter.selectedFilter?.filterName;

        return {
            id: view.filterName,
            label: view === defaultView ? t("grid_filter.no_options", "All") : view.filterName,
            onClick: () => applyView(replacesDefaultViewTab && isActiveView ? defaultView : view),
        };
    });

    useEffect(() => {
        if (selectedButton?.id !== viewFilter.selectedFilter?.filterName) {
            const newFilter =
                viewFilter.selectedFilter === undefined ? undefined : { id: viewFilter.selectedFilter.filterName };

            _setSelectedButton(newFilter);
        }
    }, [viewFilter]);

    const displayedColumnFilters = gridFilters.columnFilters.filter((v) => {
        if (v.value === null || v.value === undefined) return false;
        // Hide tag filters with no meaningful content
        if (v.id.startsWith("tag_") && typeof v.value === "object" && !Array.isArray(v.value)) {
            const tagVal = v.value as { values?: string | string[]; emptyNull?: string };
            const hasValues =
                tagVal.values !== undefined &&
                (Array.isArray(tagVal.values) ? tagVal.values.length > 0 : tagVal.values !== "");
            const hasEmptyNull = tagVal.emptyNull !== undefined && tagVal.emptyNull !== "all";
            if (!hasValues && !hasEmptyNull) return false;
        }
        return true;
    });

    return (
        <Stack gap={theme.spacing(1)}>
            <Stack
                direction="row"
                padding={theme.spacing(2)}
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={theme.spacing(1)}
            >
                <DraggableViewTabs
                    buttons={viewButtons}
                    selectedId={selectedButton?.id}
                    leading={replacesDefaultViewTab ? viewTabsLeading : undefined}
                    onReorder={(fromIndex, toIndex) => {
                        const reordered = arrayMove(viewFilter.allFilters, fromIndex, toIndex);
                        viewFilter.reorderViews(reordered);
                    }}
                />

                {/* Search box aligned to the right */}
                {searchProduct !== undefined && setSearchProduct && (
                    <Box>
                        <SearchBox
                            variant="standalone"
                            value={searchProduct}
                            placeholder={t("rechercher_un_element")}
                            onChange={setSearchProduct}
                        />
                    </Box>
                )}
                <GridTableActions actions={tableActions} appearance={tableActionsAppearance} />
            </Stack>
            {displayedColumnFilters.length > 0 && (
                <Fragment>
                    <Box
                        sx={{
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            width: "100%",
                        }}
                    />
                    <Stack flexDirection="row" padding={theme.spacing(2)} justifyContent={"space-between"}>
                        <Stack
                            flexDirection={"row"}
                            gap={theme.spacing(1)}
                            justifyContent={"start"}
                            alignItems={"center"}
                        >
                            <Typography
                                variant="body-bold"
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                }}
                            >
                                {`${t("component.grid.filters.title", "Filters")}:`}
                            </Typography>
                            <Stack flexDirection={"row"} flexWrap={"wrap"} gap={theme.spacing(1)}>
                                {displayedColumnFilters.map((columnFilter) => {
                                    const value = columnFilter.value;

                                    // Columns can opt into a custom chip value renderer when the raw
                                    // filter value is not human-readable (e.g. composite ids).
                                    const customRenderer = (
                                        columnById.get(columnFilter.id)?.meta as
                                            | { renderFilterChipValue?: (v: unknown) => ReactNode }
                                            | undefined
                                    )?.renderFilterChipValue;
                                    let displayableNode: ReactNode;

                                    // Format display value based on type
                                    let displayableValue = "";
                                    if (typeof value === "string") {
                                        displayableValue = value;
                                    } else if (Array.isArray(value)) {
                                        displayableValue = value.join(", ");
                                    } else if (value && typeof value === "object") {
                                        // Handle TagsFilterValue {values?: string | string[], emptyNull?: string}
                                        if (
                                            "values" in value ||
                                            ("emptyNull" in value && columnFilter.id.startsWith("tag_"))
                                        ) {
                                            const tagsFilter = value as TagsFilterValue;
                                            if (tagsFilter.values) {
                                                displayableValue =
                                                    Array.isArray(tagsFilter.values) ?
                                                        tagsFilter.values.join(", ")
                                                    :   tagsFilter.values;
                                            } else if (tagsFilter.emptyNull === "empty") {
                                                displayableValue = t("grid.filter.emptyNull.empty", "Vide");
                                            } else if (tagsFilter.emptyNull === "notEmpty") {
                                                displayableValue = t("grid.filter.emptyNull.notEmpty", "Non vide");
                                            }
                                        }

                                        // Handle JsonAttributeFilterValue {value?: string, min?: number, max?: number, emptyNull?: string}
                                        const jsonAttrValue = value as JsonAttributeFilterValue;
                                        if (jsonAttrValue.value) {
                                            displayableValue = jsonAttrValue.value;
                                        } else if (jsonAttrValue.min !== undefined && jsonAttrValue.max !== undefined) {
                                            displayableValue = `${jsonAttrValue.min} - ${jsonAttrValue.max}`;
                                        } else if (jsonAttrValue.min !== undefined) {
                                            displayableValue = `≥ ${jsonAttrValue.min}`;
                                        } else if (jsonAttrValue.max !== undefined) {
                                            displayableValue = `≤ ${jsonAttrValue.max}`;
                                        } else if (jsonAttrValue.emptyNull === "empty") {
                                            displayableValue = t("grid.filter.emptyNull.empty", "Vide");
                                        } else if (jsonAttrValue.emptyNull === "notEmpty") {
                                            displayableValue = t("grid.filter.emptyNull.notEmpty", "Non vide");
                                        }

                                        // Handle RangeValue {min?: number, max?: number}
                                        const rangeValue = value as RangeValue;
                                        if (
                                            !displayableValue &&
                                            rangeValue.min !== undefined &&
                                            rangeValue.max !== undefined
                                        ) {
                                            displayableValue = `${rangeValue.min} - ${rangeValue.max}`;
                                        } else if (!displayableValue && rangeValue.min !== undefined) {
                                            displayableValue = `≥ ${rangeValue.min}`;
                                        } else if (!displayableValue && rangeValue.max !== undefined) {
                                            displayableValue = `≤ ${rangeValue.max}`;
                                        }

                                        // Handle ComparisonValue {operator: string, value: number}
                                        const comparisonValue = value as ComparisonValue;
                                        if (
                                            !displayableValue &&
                                            comparisonValue.operator &&
                                            comparisonValue.value !== undefined
                                        ) {
                                            displayableValue = `${comparisonValue.operator} ${comparisonValue.value}`;
                                        }
                                    }

                                    const displayName = getColumnDisplayName(columnFilter.id);
                                    displayableNode = customRenderer ? customRenderer(value) : displayableValue;

                                    return (
                                        <Pills
                                            key={columnFilter.id}
                                            variant="brand"
                                            label={
                                                <Stack flexDirection={"row"} gap={theme.spacing(1)}>
                                                    <Typography variant="body-medium">{`${displayName} :`}</Typography>
                                                    <Typography variant="body-bold">{displayableNode}</Typography>
                                                </Stack>
                                            }
                                            onDelete={() => {
                                                const newFilters = gridFilters.columnFilters.filter(
                                                    (i) => i.id !== columnFilter.id,
                                                );
                                                gridFilters.setColumnFilters(newFilters);
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        </Stack>
                        <Stack flexDirection="row" gap={theme.spacing(1)}>
                            <Button
                                variant="text"
                                startIcon={<FoodsIcon size={1} icon="close" />}
                                onClick={viewFilter.resetSelectedFilter}
                            >
                                {t("component.grid.filters.reset", "Reset")}
                            </Button>
                            <CreateViewButton viewFilter={viewFilter} />
                        </Stack>
                    </Stack>
                </Fragment>
            )}
        </Stack>
    );
};
